import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Stack,
  Text,
  DefaultButton,
  PrimaryButton,
  IconButton,
  DetailsList,
  IColumn,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  DetailsListLayoutMode,
  SelectionMode,
  SearchBox,
  IContextualMenuProps,
  Dialog,
  DialogType,
  DialogFooter,
  mergeStyleSets,
} from "@fluentui/react";

import EditDatasetPanel from "./EditDatasetPanel";
import {
  apiDeleteDataset,
  apiGetAdminDatasets,
  type CatalogueDataset,
} from "./datasetAdminApi";
import { agentService } from "../services/agentService";
import { datasetService } from "../services/datasetService";
import type { Agent } from "../types/agent.types";

const styles = mergeStyleSets({
  // Card token set matched to AdminAccessTab so the two tabs feel identical.
  card: {
    background: "#FFFFFF",
    border: "1px solid #EAEAEA",
    borderRadius: 10,
    padding: 24,
    marginBottom: 16,
    minWidth: 0,
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  },
  cardHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
  },
  cardTitle: {
    fontSize: 17,
    fontWeight: 650,
    color: "#161617",
  },
  cardSub: {
    marginTop: 4,
    fontSize: 12,
    color: "#747474",
  },
  counterStrip: {
    fontSize: 12,
    color: "#484848",
    marginTop: 6,
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: 8,
    alignItems: "center",
    marginTop: 14,
    marginBottom: 14,
  },
  pillGroup: {
    display: "inline-flex",
    gap: 4,
    background: "#F4F5F6",
    padding: 3,
    borderRadius: 999,
  },
  filterPill: {
    padding: "5px 12px",
    fontSize: 12,
    fontWeight: 500,
    border: "1px solid transparent",
    borderRadius: 999,
    background: "transparent",
    color: "#484848",
    cursor: "pointer",
    selectors: {
      "&:hover": { background: "rgba(255,255,255,0.7)" },
    },
  },
  filterPillActive: {
    background: "#FFFFFF",
    color: "#174F20",
    border: "1px solid #B7DDB7",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },
  inlineBanner: {
    background: "#fff8e1",
    border: "1px solid #ffd54f",
    padding: "8px 12px",
    fontSize: 12,
    borderRadius: 4,
    marginBottom: 12,
  },
  pill: {
    display: "inline-block",
    padding: "1px 8px",
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 500,
    border: "1px solid #E1E1E1",
    background: "#F4F5F6",
    color: "#484848",
  },
  pillActive: {
    background: "#E8F5E9",
    color: "#107C10",
    borderColor: "rgba(16,124,16,0.3)",
  },
  pillSoon: {
    background: "#FFF4CE",
    color: "#797400",
    borderColor: "#E5CC00",
  },
  agentPill: {
    display: "inline-block",
    padding: "1px 8px",
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 500,
    background: "#EEF2FF",
    color: "#3730A3",
    border: "1px solid #C7D2FE",
  },
  muted: {
    color: "#A0A0A0",
    fontSize: 12,
  },
  metricCell: {
    fontVariantNumeric: "tabular-nums",
  },
  identityCell: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
  },
  identityName: {
    fontWeight: 600,
    color: "#161617",
    fontSize: 13,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  identityId: {
    fontSize: 11,
    color: "#747474",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
});

type Cohort = "all" | "active" | "coming_soon";

const AdminDatasetsTab: React.FC = () => {
  const [datasets, setDatasets] = useState<CatalogueDataset[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [cohort, setCohort] = useState<Cohort>("all");
  const [searchTerm, setSearchTerm] = useState("");

  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState<"add" | "edit">("edit");
  const [panelDataset, setPanelDataset] = useState<CatalogueDataset | null>(null);

  // Delete dialog. Keeping a separate target so the dialog can survive the
  // EditPanel close; the operator can also delete from the row overflow
  // menu without opening the panel first.
  const [deleteTarget, setDeleteTarget] = useState<CatalogueDataset | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadDatasets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiGetAdminDatasets();
      setDatasets(Array.isArray(data) ? data : []);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err?.response?.data?.error ?? err?.message ?? "Failed to load datasets");
    } finally {
      setLoading(false);
    }
  }, []);

  // Agents power the Agent column + the Agent dropdown inside EditDatasetPanel.
  // Best-effort: if the agent service fails the column just shows the raw id.
  const loadAgents = useCallback(async () => {
    try {
      const data = await agentService.getAgents();
      setAgents(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load agents:", e);
      setAgents([]);
    }
  }, []);

  useEffect(() => {
    loadDatasets();
    loadAgents();
  }, [loadDatasets, loadAgents]);

  const onSaved = useCallback(async () => {
    setInfo("Dataset saved.");
    await loadDatasets();
    // Best-effort refresh of the global dataset cache used elsewhere in the
    // portal (Datasets page, dashboard hub). Non-fatal if it fails.
    try { await datasetService.getDatasets(); } catch { /* ignore */ }
  }, [loadDatasets]);

  const openAdd = useCallback(() => {
    setPanelDataset(null);
    setPanelMode("add");
    setPanelOpen(true);
  }, []);

  const openEdit = useCallback((ds: CatalogueDataset) => {
    setPanelDataset(ds);
    setPanelMode("edit");
    setPanelOpen(true);
  }, []);

  const askDelete = useCallback((ds: CatalogueDataset) => {
    setDeleteTarget(ds);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiDeleteDataset(deleteTarget.id);
      setDeleteTarget(null);
      setInfo("Dataset deleted.");
      await loadDatasets();
      try { await datasetService.getDatasets(); } catch { /* ignore */ }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err?.response?.data?.error ?? err?.message ?? "Failed to delete dataset");
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, loadDatasets]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return datasets.filter((d) => {
      if (cohort !== "all" && d.status !== cohort) return false;
      if (q) {
        const hay = `${d.department} ${d.id} ${d.owner} ${d.ownerEmail}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [datasets, cohort, searchTerm]);

  const cohortCounts = useMemo(() => ({
    all: datasets.length,
    active: datasets.filter((d) => d.status === "active").length,
    coming_soon: datasets.filter((d) => d.status === "coming_soon").length,
  }), [datasets]);

  const totalTables = useMemo(
    () => datasets.reduce((acc, d) => acc + (d.tables?.length ?? 0), 0),
    [datasets]
  );

  // Row overflow menu — Edit / Copy ID / Delete.
  const rowMenu = useCallback((item: CatalogueDataset): IContextualMenuProps => ({
    items: [
      {
        key: "edit", text: "Edit dataset", iconProps: { iconName: "Edit" },
        onClick: () => { openEdit(item); return true; },
      },
      {
        key: "copyId", text: "Copy ID", iconProps: { iconName: "Copy" },
        onClick: () => {
          if (typeof navigator !== "undefined" && navigator.clipboard) {
            navigator.clipboard.writeText(item.id).catch(() => {});
          }
          return true;
        },
      },
      { key: "divider", itemType: 1 as const },
      {
        key: "delete", text: "Delete dataset", iconProps: { iconName: "Cancel" },
        onClick: () => { askDelete(item); return true; },
      },
    ],
    directionalHintFixed: true,
  }), [openEdit, askDelete]);

  const columns: IColumn[] = useMemo(
    () => [
      {
        key: "department",
        name: "Dataset",
        minWidth: 220,
        isResizable: true,
        onRender: (ds: CatalogueDataset) => (
          <div className={styles.identityCell}>
            <div style={{ minWidth: 0 }}>
              <div className={styles.identityName} title={ds.department}>{ds.department}</div>
              <div className={styles.identityId} title={ds.id}>{ds.id}</div>
            </div>
          </div>
        ),
      },
      {
        key: "status",
        name: "Status",
        minWidth: 100,
        maxWidth: 110,
        onRender: (ds: CatalogueDataset) => (
          <span
            className={`${styles.pill} ${ds.status === "active" ? styles.pillActive : styles.pillSoon}`}
          >
            {ds.status === "active" ? "Active" : "Coming soon"}
          </span>
        ),
      },
      {
        key: "tables",
        name: "Tables",
        minWidth: 70,
        maxWidth: 90,
        onRender: (ds: CatalogueDataset) => {
          const n = (ds.tables ?? []).length;
          return n === 0
            ? <span className={styles.muted}>—</span>
            : <span className={`${styles.pill} ${styles.metricCell}`}>{n}</span>;
        },
      },
      {
        key: "agent",
        name: "Agent",
        minWidth: 140,
        isResizable: true,
        onRender: (ds: CatalogueDataset) => {
          if (!ds.agentId) return <span className={styles.muted}>None</span>;
          const agent = agents.find((a) => a.id === ds.agentId);
          return <span className={styles.agentPill}>{agent?.name || ds.agentId}</span>;
        },
      },
      {
        key: "classification",
        name: "Classification",
        minWidth: 140,
        onRender: (ds: CatalogueDataset) => (
          <span className={styles.pill}>{ds.classification}</span>
        ),
      },
      {
        key: "owner",
        name: "Owner",
        minWidth: 160,
        isResizable: true,
        onRender: (ds: CatalogueDataset) => (
          ds.owner
            ? <div>
                <div style={{ fontSize: 12, color: "#232323" }}>{ds.owner}</div>
                {ds.ownerEmail && <div style={{ fontSize: 11, color: "#747474" }}>{ds.ownerEmail}</div>}
              </div>
            : <span className={styles.muted}>—</span>
        ),
      },
      {
        key: "actions",
        name: "",
        minWidth: 64,
        maxWidth: 64,
        onRender: (ds: CatalogueDataset) => (
          <Stack horizontal tokens={{ childrenGap: 2 }} verticalAlign="center">
            <IconButton
              iconProps={{ iconName: "Edit" }}
              title="Edit dataset"
              ariaLabel="Edit dataset"
              onClick={(e) => { e?.stopPropagation(); openEdit(ds); }}
              styles={{ root: { color: "#484848", height: 28, width: 28 } }}
            />
            <IconButton
              iconProps={{ iconName: "More" }}
              title="More actions"
              ariaLabel="More actions"
              menuProps={rowMenu(ds)}
              menuIconProps={{ style: { display: "none" } }}
              styles={{ root: { color: "#484848", height: 28, width: 28 } }}
            />
          </Stack>
        ),
      },
    ],
    [agents, openEdit, rowMenu]
  );

  return (
    <Stack tokens={{ childrenGap: 0 }}>
      {error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline
          onDismiss={() => setError(null)}
          dismissButtonAriaLabel="Close"
          styles={{ root: { marginBottom: 12 } }}
        >
          {error}
        </MessageBar>
      )}
      {info && (
        <MessageBar
          messageBarType={MessageBarType.success}
          isMultiline
          onDismiss={() => setInfo(null)}
          dismissButtonAriaLabel="Close"
          styles={{ root: { marginBottom: 12 } }}
        >
          {info}
        </MessageBar>
      )}

      <div className={styles.card}>
        <div className={styles.cardHeaderRow}>
          <div style={{ minWidth: 0 }}>
            <Text className={styles.cardTitle}>Dataset catalog</Text>
            <Text className={styles.cardSub}>
              Department datasets, table assignments, and agent mapping.
            </Text>
            <div className={styles.counterStrip}>
              {cohortCounts.all} datasets · {cohortCounts.active} active ·
              {" "}{cohortCounts.coming_soon} coming soon · {totalTables} tables mapped
            </div>
          </div>
        </div>

        <div className={styles.toolbar}>
          <div className={styles.pillGroup}>
            {([
              { key: "all", label: `All (${cohortCounts.all})` },
              { key: "active", label: `Active (${cohortCounts.active})` },
              { key: "coming_soon", label: `Coming soon (${cohortCounts.coming_soon})` },
            ] as { key: Cohort; label: string }[]).map((p) => (
              <button
                key={p.key}
                type="button"
                className={`${styles.filterPill} ${cohort === p.key ? styles.filterPillActive : ""}`}
                onClick={() => setCohort(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <SearchBox
            placeholder="Search by department, ID, or owner"
            value={searchTerm}
            onChange={(_, v) => setSearchTerm(v ?? "")}
            styles={{ root: { width: 280 } }}
          />
          <span style={{ flex: 1 }} />
          <PrimaryButton iconProps={{ iconName: "Add" }} text="Add dataset" onClick={openAdd} />
          <IconButton
            iconProps={{ iconName: "Refresh" }}
            title="Refresh"
            ariaLabel="Refresh datasets"
            onClick={loadDatasets}
            styles={{ root: { color: "#484848", height: 32, width: 32 } }}
          />
        </div>

        {loading ? (
          <Spinner size={SpinnerSize.medium} label="Loading datasets…" />
        ) : (
          <div>
            <DetailsList
              items={filtered}
              columns={columns}
              selectionMode={SelectionMode.none}
              layoutMode={DetailsListLayoutMode.justified}
              onItemInvoked={(item) => openEdit(item as CatalogueDataset)}
            />
            {filtered.length === 0 && (
              <div style={{ padding: 32, textAlign: "center", color: "#6B6B73" }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: "#161617", marginBottom: 4 }}>
                  No datasets match
                </div>
                <div style={{ fontSize: 12 }}>Try a different filter or clear the search.</div>
              </div>
            )}
          </div>
        )}
      </div>

      <EditDatasetPanel
        open={panelOpen}
        mode={panelMode}
        dataset={panelDataset}
        agents={agents}
        onClose={() => setPanelOpen(false)}
        onSaved={onSaved}
      />

      <Dialog
        hidden={!deleteTarget}
        onDismiss={() => !deleting && setDeleteTarget(null)}
        dialogContentProps={{
          type: DialogType.normal,
          title: "Delete dataset?",
          subText: deleteTarget
            ? `"${deleteTarget.department}" will be deleted permanently. Any user grants and dashboards that reference it will need to be updated separately.`
            : "",
        }}
      >
        <DialogFooter>
          <PrimaryButton
            text={deleting ? "Deleting…" : "Delete dataset"}
            onClick={confirmDelete}
            disabled={deleting}
            styles={{
              root: { background: "#A4262C", borderColor: "#A4262C" },
              rootHovered: { background: "#8E1F25", borderColor: "#8E1F25" },
            }}
          />
          <DefaultButton text="Cancel" onClick={() => setDeleteTarget(null)} disabled={deleting} />
        </DialogFooter>
      </Dialog>
    </Stack>
  );
};

export default AdminDatasetsTab;
